import type { VoiceNoteContentType } from '../api/types';

/**
 * What `MediaRecorder` can actually encode, ranked.
 *
 * Chrome, Firefox and Edge record `audio/webm` (Opus). Safari records
 * neither webm nor Opus and answers `audio/mp4` instead, which is why the
 * server's `VoiceNoteContentType` (INTERFACES.md 15.3) names both. The
 * first candidate this browser's `MediaRecorder` reports as supported wins,
 * so a device gets the format its own encoder actually produces rather
 * than one this client guessed at from its user agent.
 */
export interface VoiceMimeCandidate {
  mimeType: string;
  contentType: VoiceNoteContentType;
}

const CANDIDATES: VoiceMimeCandidate[] = [
  { mimeType: 'audio/webm;codecs=opus', contentType: 'audio/webm' },
  { mimeType: 'audio/webm', contentType: 'audio/webm' },
  { mimeType: 'audio/mp4', contentType: 'audio/mp4' },
  { mimeType: 'audio/ogg;codecs=opus', contentType: 'audio/ogg' },
  { mimeType: 'audio/ogg', contentType: 'audio/ogg' },
];

function defaultIsTypeSupported(mimeType: string): boolean {
  return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mimeType);
}

/** The first candidate this runtime's encoder supports, or null on a
 *  browser (or test environment) with no usable candidate at all. */
export function pickVoiceMimeType(
  isTypeSupported: (mimeType: string) => boolean = defaultIsTypeSupported,
): VoiceMimeCandidate | null {
  return CANDIDATES.find((candidate) => isTypeSupported(candidate.mimeType)) ?? null;
}
